import * as React from 'react'
import { Helmet } from 'react-helmet-async'
import { withPrefix } from 'gatsby'
// import useSiteMetadata
import useSiteMetadata from '../hooks/use-site-metadata'

type Props = {
  title?: string
  useTitleTemplate?: boolean
  description?: string
  pathname?: string
  image?: string
  type?: 'website' | 'article'
  publishedTime?: string
  modifiedTime?: string
  children?: React.ReactNode
  noindex?: boolean
  nofollow?: boolean
}

const SEO = ({
  title = ``,
  useTitleTemplate = true,
  description = ``,
  pathname = ``,
  image = ``,
  type = 'website',
  publishedTime,
  modifiedTime,
  children = null,
  noindex = false,
  nofollow = false,
}: Props) => {
  const site = useSiteMetadata()

  const {
    siteTitle,
    siteTitleAlt: defaultTitle,
    siteUrl,
    siteDescription: defaultDescription,
    siteKeywords,
    siteLanguage,
    siteImage: defaultImage,
    author,
  } = site

  const normalizedSiteUrl = (siteUrl || '').replace(/\/$/, '')
  // Normalize internal pathnames to start and end with '/'
  const resolvedPathname = pathname
    ? pathname.startsWith('http')
      ? pathname
      : (() => {
          const withLeading = pathname.startsWith('/')
            ? pathname
            : `/${pathname}`
          if (withLeading === '/') return withLeading
          return withLeading.endsWith('/') ? withLeading : `${withLeading}/`
        })()
    : ''
  const fullUrl = resolvedPathname
    ? `${normalizedSiteUrl}${resolvedPathname}`
    : normalizedSiteUrl
  const resolvedImage =
    image && image.startsWith('http')
      ? image
      : `${normalizedSiteUrl}${image || defaultImage}`
  const resolvedTitle =
    title && useTitleTemplate ? `${title} | ${siteTitle}` : title || defaultTitle

  const seo = {
    title: resolvedTitle,
    description: description || defaultDescription,
    url: fullUrl,
    image: resolvedImage,
  }
  return (
    <Helmet
      title={title}
      defaultTitle={defaultTitle}
      titleTemplate={useTitleTemplate ? `%s | ${siteTitle}` : undefined}
    >
      <html lang={siteLanguage} />
      <meta name="description" content={seo.description} />
      {siteKeywords ? <meta name="keywords" content={siteKeywords} /> : null}
      <meta name="image" content={seo.image} />
      <meta property="og:title" content={seo.title} />
      <meta property="og:url" content={seo.url} />
      <meta property="og:description" content={seo.description} />
      <meta property="og:image" content={seo.image} />
      <meta property="og:type" content={type} />
      {type === 'article' && publishedTime ? (
        <meta property="article:published_time" content={publishedTime} />
      ) : null}
      {type === 'article' && modifiedTime ? (
        <meta property="article:modified_time" content={modifiedTime} />
      ) : null}
      <meta property="og:site_name" content={siteTitle} />
      <meta property="og:image:alt" content={seo.description} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={seo.title} />
      <meta name="twitter:url" content={seo.url} />
      <meta name="twitter:description" content={seo.description} />
      <meta name="twitter:image" content={seo.image} />
      <meta name="twitter:image:alt" content={seo.description} />
      <meta name="twitter:creator" content={author} />
      {/* robots policy (can be overridden by children) */}
      <meta
        name="robots"
        content={`${noindex ? 'noindex' : 'index'}, ${
          nofollow ? 'nofollow' : 'follow'
        }, max-image-preview:large, max-snippet:-1, max-video-preview:-1`}
      />
      {/* canonical url */}
      {seo.url ? <link rel="canonical" href={seo.url} /> : null}
      <link
        rel="icon"
        type="image/png"
        sizes="32x32"
        href={withPrefix(`/favicon-32x32.png`)}
      />
      <link
        rel="icon"
        type="image/png"
        sizes="16x16"
        href={withPrefix(`/favicon-16x16.png`)}
      />
      <link
        rel="apple-icon"
        sizes="180x180"
        href={withPrefix(`/apple-icon-180x180.png`)}
      />
      <link
        rel="shortcut icon"
        href={withPrefix(`/favicon.ico`)}
        type="image/x-icon"
      />
      {children}
    </Helmet>
  )
}

export default SEO
